import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Schema } from '../../../types/typeHelper';
import type { FetchETSTicketNotificationsResponseData } from '../../clients/api/entities/tickets/tickets-api-helpers';
import { TICKET_VIEWS } from './ticketsConstants';
import { newTicketFromMessages } from './ticketsThunks';

type Prefill =
  | {
      description: string | undefined;
      files?: {
        url: string;
        name: string;
        type: string;
      }[];
    }
  | undefined;

export interface TicketsState {
  drafts: {
    [key: string]: {
      description?: string | undefined;
      customerOrderText?: string | undefined;
      attachments?: File[] | undefined;
      erpCustomerId?: string | undefined;
      inputChannel?: Schema<'EtsInputChannelEnum'>;
    };
  };
  ticketView: {
    [key: string]:
      | {
          view: (typeof TICKET_VIEWS)[keyof typeof TICKET_VIEWS];
          ticketId?: string;
          firstTime?: boolean;
          prefill?: Prefill;
        }
      | undefined;
  };
  ticketNotifications: FetchETSTicketNotificationsResponseData['notifications'];
}

const initialState: TicketsState = {
  ticketView: {}, // selected view per channel [channelId] => { view: 'form' }
  // TODO remove later when feasible
  // Currently coupled with other redux selectors and cannot use react-query
  ticketNotifications: [], // notifications array [...ticketNotifications]
  drafts: {}, // ticket form drafts by channel id [channelId] => {...formValues}
};

const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    ticketIdSelected: (
      state,
      action: PayloadAction<{
        channelId?: string;
        ticketId: string;
        firstTime: boolean;
      }>,
    ) => {
      const { channelId, ticketId, firstTime } = action.payload;

      state.ticketView[channelId || ''] = {
        view: TICKET_VIEWS.DETAILS,
        ticketId,
        firstTime,
      };
    },
    createNewTicketSelected: (state, action: PayloadAction<{ channelId?: string | undefined }>) => {
      const { channelId } = action.payload || {};

      state.ticketView[channelId || ''] = {
        view: TICKET_VIEWS.FORM,
      };
    },
    ticketFormChanged: (
      state,
      action: PayloadAction<{
        channelId?: string | undefined;
        ticketId?: string | undefined;
        formValues?: Partial<TicketsState['drafts'][string] | undefined>;
      }>,
    ) => {
      const { channelId, ticketId, formValues } = action.payload || {};

      if (!formValues) {
        delete state.drafts[channelId || ticketId || ''];
      } else {
        state.drafts[channelId || ticketId || ''] = formValues;
      }
    },
    backToTicketsOverview: (state, action: PayloadAction<{ channelId?: string | undefined }>) => {
      const { channelId } = action.payload || {};

      state.ticketView[channelId || ''] = undefined;
    },
    clearFormPrefill: (state, action: PayloadAction<{ channelId?: string | undefined }>) => {
      let { channelId } = action.payload;
      channelId ||= '';

      const ticketView = state.ticketView[channelId];
      if (ticketView) {
        ticketView.prefill = undefined;
      }
    },
    // TODO remove later when feasible
    // Currently coupled with other redux selectors and cannot use react-query
    setTicketNotifications: (state, action: PayloadAction<TicketsState['ticketNotifications']>) => {
      state.ticketNotifications = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      newTicketFromMessages.fulfilled,
      (
        state,
        action: PayloadAction<
          {
            messages?: { text: string }[];
            files: { url: string; name: string; type: string }[];
          },
          string,
          { arg: { channelId?: string | undefined } }
        >,
      ) => {
        const {
          payload: { messages, files },
          meta: {
            arg: { channelId },
          },
        } = action;
        state.ticketView[channelId || ''] = {
          view: TICKET_VIEWS.FORM,
          prefill: {
            description: messages
              ?.map((message) => message.text)
              .filter((text) => !!text)
              .join('\n\n'),
            files,
          },
        };
      },
    );
  },
});

export const {
  ticketIdSelected,
  backToTicketsOverview,
  createNewTicketSelected,
  ticketFormChanged,
  clearFormPrefill,
  setTicketNotifications,
} = ticketsSlice.actions;

export default ticketsSlice.reducer;
